import { TIER_COLORS } from './SeasonStrip';

export interface MedalCounts {
  platinum: number;
  gold: number;
  silver: number;
  bronze: number;
}

const SHELF: Array<{ key: keyof MedalCounts; name: string; color: string }> = [
  { key: 'platinum', name: 'Platinum', color: TIER_COLORS.Platinum },
  { key: 'gold', name: 'Gold', color: TIER_COLORS.Gold },
  { key: 'silver', name: 'Silver', color: TIER_COLORS.Silver },
  { key: 'bronze', name: 'Bronze', color: TIER_COLORS.Bronze },
];

/** Circle-plus-ribbon medal glyph — matches `TierMedal` in SeasonStrip.tsx
 *  (kept local rather than imported: that component isn't exported, and
 *  this task's file list doesn't include modifying SeasonStrip.tsx). */
function MedalGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="9" r="6" />
      <path d="M8.5 14.5 7 22l5-2.5L17 22l-1.5-7.5" />
    </svg>
  );
}

/**
 * The profile page's medal shelf: four tiles, always in Platinum → Gold →
 * Silver → Bronze order, each showing its count. A tier the player has
 * never earned still renders its tile (count 0) rather than being omitted
 * — dimmed, per the mockup, so an empty shelf reads as "not yet" rather
 * than looking broken or missing data.
 */
export function MedalShelf({ medals }: { medals: MedalCounts }) {
  return (
    <div
      data-testid="medal-shelf"
      className="overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]"
    >
      <p className="px-4 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
        Medal shelf
      </p>
      <div className="flex gap-2.5 px-4 pb-4 pt-2.5">
        {SHELF.map(({ key, name, color }) => {
          const count = medals[key];
          const dimmed = count === 0;
          return (
            <div
              key={key}
              data-testid={`medal-tile-${key}`}
              className="flex flex-1 flex-col items-center gap-1.5 rounded-md border border-tile-edge/60 bg-tile-face/35 px-1.5 py-3"
            >
              <span
                aria-hidden="true"
                className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[#fffdf6]"
                style={{ backgroundColor: color, opacity: dimmed ? 0.25 : 1 }}
              >
                <MedalGlyph />
              </span>
              <span
                data-testid={`medal-count-${key}`}
                className={`text-lg font-bold leading-none tabular-nums ${
                  dimmed ? 'text-stone-400' : 'text-stone-900'
                }`}
              >
                {count}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-stone-600">
                {name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
